import { ApolloError } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { useCallback } from 'react';
import { SetOptional } from 'type-fest';

import { Button } from '@/components/form/baseInputs/Button';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';

import { Operations as CollaboratorsOperations } from '../graphql/CollaboratorsPage.generated';
import { CollaboratorForm } from './CollaboratorForm';
import {
  CollaboratorFormShape,
  defaultValues,
} from './CollaboratorModal.constants';
import {
  mapFormDataToUpdateInput,
  useSyncCollaboratorDataToForm,
} from './EditCollaboratorModal.utils';
import { useUpdateCollaboratorMutation } from './graphql/CollaboratorModal.generated';
import { useDeleteCollaboratorMutation } from './graphql/DeleteCollaborator.generated';
import { useReinviteCollaboratorMutation } from './graphql/ReinviteCollaborator.generated';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'> & {
  collaboratorId: string;
};
export type EditCollaboratorModalProps = FormModalOptionalProps;

function EditCollaborator({
  onClose,
  heading = 'Edit collaborator',
  collaboratorId,
  ...formModalProps
}: FormModalOptionalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const canEditCollaborator = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_EDIT_COLLABORATORS
  );
  const canViewHouseholds = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_VIEW_HOUSEHOLDS,
    { detailed: false }
  );
  const { handleSubmit, reset, shouldBlockNavigation, formState, getValues } =
    useFormContext<CollaboratorFormShape>();

  const [updateMutation, { loading: isSubmitting }] =
    useUpdateCollaboratorMutation({
      refetchQueries: 'active',
      awaitRefetchQueries: true,
    });

  const [reinviteCollaborator, { loading: isResending }] =
    useReinviteCollaboratorMutation({
      onError: (error) => {
        showFeedback('Failed to reinvite collaborator.');
        reportError('Could not reinvite collaborator', error);
      },
      onCompleted: () => {
        showFeedback('Successfully sent invitation email.', {
          variant: 'success',
        });
        onClose();
      },
    });

  const [deleteCollaborator, { loading: isDeleting }] =
    useDeleteCollaboratorMutation({
      refetchQueries: [CollaboratorsOperations.Query.GetCollaborators],
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError('Could not delete collaborator', error);
      },
      onCompleted: () => {
        showFeedback('Collaborator deleted successfully.', {
          variant: 'success',
        });
        onClose();
      },
    });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      await updateMutation({
        variables: {
          input: mapFormDataToUpdateInput(
            formData,
            collaboratorId,
            !!canViewHouseholds
          ),
        },
      });
    } catch (error) {
      const userFacingMessages =
        error instanceof ApolloError ? getUserFacingErrorMessages(error) : [];
      showFeedback(
        userFacingMessages.length > 0
          ? userFacingMessages.join('. ')
          : FeedbackMessages.formSaveError
      );
      reportError('Could not update collaborator', error as Error);
      // Re-throw so react-hook-form marks submission as failed
      // and useSubmitSuccessHandler doesn't close the modal
      throw error;
    }
  });

  const handleReinviteUser = useCallback(async () => {
    const email = getValues('email');
    void reinviteCollaborator({
      variables: {
        email,
      },
    });
  }, [getValues, reinviteCollaborator]);

  const handleDeleteCollaborator = useCallback(async () => {
    void deleteCollaborator({
      variables: {
        collaboratorId,
      },
    });
  }, [deleteCollaborator, collaboratorId]);

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormFieldsDisabledProvider isDisabled={!canEditCollaborator}>
      <FormModal
        {...formModalProps}
        onClose={onClose}
        heading={heading}
        shouldBlockClose={shouldBlockNavigation}
        actions={
          <FormModalActions.Provider
            formState={formState}
            viewOnly={!canEditCollaborator}
          >
            {canEditCollaborator && (
              <Stack spacing={0.5} direction="row">
                <FormModalActions.DeleteButton
                  onConfirmDelete={handleDeleteCollaborator}
                  loading={isDeleting}
                  disabled={isSubmitting || isResending}
                  promptDeleteText="Delete collaborator"
                  confirmDeleteText="Confirm delete"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleReinviteUser}
                  disabled={isSubmitting || isDeleting}
                  loading={isResending}
                >
                  Resend invite
                </Button>
              </Stack>
            )}
            <Box flexGrow={1} />
            <FormModalActions.CancelButton onClick={closeModal} />
            <FormModalActions.SaveButton
              disabled={isDeleting || isResending}
              onClick={handleSave}
            >
              Save changes
            </FormModalActions.SaveButton>
          </FormModalActions.Provider>
        }
      >
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <CollaboratorForm variant="edit" />
        </form>
      </FormModal>
    </FormFieldsDisabledProvider>
  );
}

export const EditCollaboratorModal = ({
  collaboratorId,
  onClose,
  ...props
}: EditCollaboratorModalProps) => {
  const formMethods = useForm<CollaboratorFormShape>({ defaultValues });
  useSyncCollaboratorDataToForm(formMethods.reset, collaboratorId, onClose);

  return (
    <FormProvider {...formMethods}>
      <EditCollaborator
        {...props}
        collaboratorId={collaboratorId}
        onClose={onClose}
      />
    </FormProvider>
  );
};
